import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/button';
import Stats from '../../components/Stats';
import NewTableComponent from '../../components/NewTableComponent';
import UserAvatar from '../../components/UserAvatar';
import MultiLineText from '../../components/MultiLineText';
import { RiDeleteBinLine } from 'react-icons/ri';
import { FiArrowLeft, FiSearch, FiCalendar, FiUsers, FiUser, FiEye, FiDownload, FiPhone, FiMessageSquare, FiMapPin, FiChevronRight, FiChevronDown, FiClock, FiFileText, FiBarChart2 } from 'react-icons/fi';
import { FaFilePdf, FaFileExcel } from 'react-icons/fa';

import { activeStatsIcon, lateStatsIcon, statsCompletedIcon, tripStatsIcon } from '../../utils/svgs';
import FleetMonitoring from './FleetMonitoring';
import CustomTabs from '../../components/CustomTabs';
import Select from '../../components/Select';
import { apiService } from '../../services/apiService';
import { AppMainModules, FMCRPermission, FMCRSubModules } from '../../utils/app-constant';
import { hasFullPermission } from '../../utils/helper';
import '../../styles/FMCR.css';
import '../../styles/Access.css';

const initialData = [
  {
    id: 1,
    reportId: "RPT-2825-0129",
    reportTitle: "Weekly Alerts Digest",
    scope: "Available · Past week",
    generatedOn: "2026-05-15",
    assignedDriver: "Noman Aslam",
    format: "PDF",
  },
  {
    id: 2,
    reportId: "RPT-2825-0128",
    reportTitle: "Weekly Fleet Performance — Active Vehicles",
    scope: "All Vehicles - Weekly",
    generatedOn: "2026-03-10",
    assignedDriver: "Waqar Siddiq",
    format: "PDF",
  },
  {
    id: 3,
    reportId: "RPT-2825-0127",
    reportTitle: "Idle Activity Investigation — KHI-9034-F",
    scope: "Active Fleets · Jun 2 - Jun 9",
    generatedOn: "2026-07-01",
    assignedDriver: "Sajid Khan",
    format: "PDF",
  },
  {
    id: 4,
    reportId: "RPT-2825-0126",
    reportTitle: "Alerts Summary — Last 24 Hours",
    scope: "Specific Vehicle · Last 7 days",
    generatedOn: "2026-02-28",
    assignedDriver: "Tariq Ali",
    format: "PDF",
  },
  {
    id: 5,
    reportId: "RPT-2825-0125",
    reportTitle: "Driver Compliance Audit — Q2 Review",
    scope: "Available Vehicles",
    generatedOn: "2026-09-12",
    assignedDriver: "Kamran Akmal",
    format: "PDF",
  },
  {
    id: 6,
    reportId: "RPT-2825-0124",
    reportTitle: "Driver Connectivity Check - All Drivers",
    scope: "Specific Trip",
    generatedOn: "2026-01-20",
    assignedDriver: "Faisal Rauf",
    format: "PDF",
  },
  {
    id: 7,
    reportId: "RPT-2825-0123",
    reportTitle: "Trip Reconciliation — TRP-2024-8841",
    scope: "Specific Duration",
    generatedOn: "2026-06-18",
    assignedDriver: "Irfan Malik",
    format: "PDF",
  },
  {
    id: 8,
    reportId: "RPT-2825-0122",
    reportTitle: "Fuel Consumption Analysis — May 2025",
    scope: "Specific Duration",
    generatedOn: "2026-04-22",
    assignedDriver: "Noman Aslam",
    format: "PDF",
  },
  {
    id: 9,
    reportId: "RPT-2825-0121",
    reportTitle: "Hbo Activity Report",
    scope: "Active Fleets · Jun 2 - Jun 9",
    generatedOn: "2026-06-19",
    assignedDriver: "Waqar Siddiq",
    format: "PDF",
  }
];

const FMCR = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const canViewTracking = hasFullPermission({
    permissionSlug: [FMCRPermission.Tracking, FMCRPermission.ViewTracking],
    menuSlug: FMCRSubModules.FMCR,
    moduleSlug: AppMainModules.fmcr,
  });

  const canViewReports = hasFullPermission({
    permissionSlug: [FMCRPermission.Reports, FMCRPermission.ViewReport],
    menuSlug: FMCRSubModules.FMCR,
    moduleSlug: AppMainModules.fmcr,
  });
  const canCreateReport = hasFullPermission({
    permissionSlug: [FMCRPermission.CreateReport, FMCRPermission.Reports, FMCRPermission.ViewReport],
    menuSlug: FMCRSubModules.FMCR,
    moduleSlug: AppMainModules.fmcr,
  });

  const initialView = canViewTracking ? "tracking" : (canViewReports ? "reports" : "");
  const [activeView, setActiveView] = useState(initialView); // 'reports', 'tracking', 'details'

  const [apiReports, setApiReports] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canViewTracking && canViewReports && activeView === "tracking") {
      setActiveView("reports");
    } else if (!canViewReports && canViewTracking && activeView === "reports") {
      setActiveView("tracking");
    }
  }, [canViewTracking, canViewReports, activeView]);

  useEffect(() => {
    if (location.state?.activeView) {
      setActiveView(location.state.activeView);
      window.history.replaceState({}, document.title);
    } else if (!canViewTracking && canViewReports && activeView === "tracking") {
      setActiveView("reports");
    } else if (!canViewReports && canViewTracking && activeView === "reports") {
      setActiveView("tracking");
    }
  }, [location.state, canViewTracking, canViewReports, activeView]);

  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Statistics cards data based on Figma
  const fmcrStatsData = [
    {
      title: "Total Fleet",
      value: "40,689",
      text: (
        <>
          <b>8.5% </b>
          <span>Up from yesterday</span>
        </>
      ),
      icon: tripStatsIcon,
    },
    {
      title: "Active Fleet",
      value: "35,420",
      text: (
        <>
          <b>12.3% </b>
          <span>Up from yesterday</span>
        </>
      ),
      icon: statsCompletedIcon,
      className: "completed",
    },
    {
      title: "Maintenance",
      value: "4,215",
      text: (
        <>
          <b>5.1% </b>
          <span>Up from yesterday</span>
        </>
      ),
      icon: lateStatsIcon,
      className: "late",
    },
    {
      title: "Total Cost",
      value: "106.8M",
      text: (
        <>
          <b>15.2% </b>
          <span>Up from yesterday</span>
        </>
      ),
      icon: activeStatsIcon,
      className: "pending-delivery",
    },
  ];

  // Helper function to map API results to expected layout format
  const mapApiResponseToReports = (results) => {
    if (!Array.isArray(results)) return [];
    return results.map((item) => {
      const generatedOn = item.generated_at
        ? item.generated_at.split('T')[0]
        : (item.created_at ? item.created_at.split('T')[0] : "-");

      let scopeVal = String(item.report_type || "")
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      if (scopeVal === "All Vehicles") {
        scopeVal = "All Vehicles · Last 30 Days";
      } else if (scopeVal === "Active Fleets") {
        scopeVal = "Active Fleets · Jun 2 - Jun 9";
      } else if (scopeVal === "Available Vehicles") {
        scopeVal = "Available Vehicles · Last 7 Days";
      } else if (scopeVal === "Specific Trip") {
        scopeVal = "Specific Trip";
      } else if (scopeVal === "Specific Duration") {
        scopeVal = "Specific Duration";
      }

      return {
        id: item.id,
        reportId: item.id,
        reportTitle: item.title || "HBO Activity Report",
        scope: scopeVal,
        generatedOn: generatedOn,
        assignedDriver: item.user?.username || "Admin",
        format: String(item.file_extension || "PDF").toUpperCase(),
        file_url: item.file_url || ""
      };
    });
  };

  // Fetch paginated reports from API
  useEffect(() => {
    let active = true;
    const fetchReports = async () => {
      setLoading(true);
      try {
        const response = await apiService.getFmcrReportsPaginated({
          page: currentPage,
          limit: limit,
          search: searchText
        });
        if (active && response && response.data) {
          const results = response.data.results || response.data.data?.results || [];
          const mapped = mapApiResponseToReports(results);
          setApiReports(mapped);

          const metaTotal = response.data.meta?.totalItems || response.data.total || mapped.length;
          setTotalItems(metaTotal);
        }
      } catch (err) {
        console.error("Error fetching paginated reports:", err);
      } finally {
        if (active) setLoading(false);
      }
    };
    if (activeView === "reports") {
      fetchReports();
    }
    return () => {
      active = false;
    };
  }, [currentPage, limit, searchText, activeView]);

  // Local sorting
  const sortedData = useMemo(() => {
    let result = [...apiReports];

    if (sortColumn) {
      result.sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [apiReports, sortColumn, sortDirection]);

  const commonColumns = {
    checked: true,
    disabled: true,
    hide: false,
    exportColumn: true,
    sortable: false,
  };

  const columns = [
    {
      ...commonColumns,
      name: "Report ID",
      selector: (row) => (
        <span
          className="hyperlink"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/fmcr/report-details/${row.reportId}`)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              navigate(`/fmcr/report-details/${row.reportId}`);
            }
          }}
        >
          {row.reportId}
        </span>
      ),
      tableColumn: true,
      filterName: "reportIdFilter",
      type: "text",
      minWidth: "150px",
    },
    {
      ...commonColumns,
      name: "Report",
      selector: (row) => row.reportTitle,
      cell: (row) => (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`tooltip-report-${row.id}`}>{row.reportTitle}</Tooltip>}
        >
          <span style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
            cursor: 'pointer'
          }}>
            {row.reportTitle}
          </span>
        </OverlayTrigger>
      ),
      tableColumn: true,
      filterName: "reportTitleFilter",
      type: "text",
      minWidth: "220px",
    },
    {
      ...commonColumns,
      name: "Scope",
      selector: (row) => row.scope,
      cell: (row) => (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`tooltip-scope-${row.id}`}>{row.scope}</Tooltip>}
        >
          <span style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
            display: 'block',
            cursor: 'pointer'
          }}>
            {row.scope}
          </span>
        </OverlayTrigger>
      ),
      tableColumn: true,
      filterName: "scopeFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      ...commonColumns,
      name: "Generated On",
      selector: (row) => row.generatedOn,
      tableColumn: true,
      filterName: "generatedOnFilter",
      type: "date",
      minWidth: "130px",
    },
    {
      ...commonColumns,
      name: "Generated By",
      selector: (row) => (
        <div className="asset_driver_cell">
          <UserAvatar username={row.assignedDriver} marginRight="0px" />
          <span className="asset_driver_name">{row.assignedDriver || "-"}</span>
        </div>
      ),
      tableColumn: true,
      filterName: "generatedByFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      ...commonColumns,
      name: "Format",
      selector: (row) => "PDF",
      cell: (row) => {
        const handleDownload = (e) => {
          e.stopPropagation();
          if (row.file_url) {
            const link = document.createElement('a');
            const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
            const absoluteUrl = row.file_url.startsWith('http')
              ? row.file_url
              : `${baseUrl.replace(/\/api\/?$/, '')}/${row.file_url}`;

            link.href = absoluteUrl;
            link.target = '_blank';
            link.download = `${row.reportTitle || 'report'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            alert("No download URL available for this report.");
          }
        };

        return (
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: 'pointer' }}
            onClick={handleDownload}
            title="Download PDF Report"
          >
            <FaFilePdf style={{ color: '#E04006', fontSize: '18px' }} />
            <span style={{ color: '#3d3f5c', fontSize: '13px', fontWeight: 500 }}>PDF</span>
          </div>
        );
      },
      tableColumn: true,
      sortable: false,
      minWidth: "120px",
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="table_action_btn">
          <button
            className="delete_btn"
            onClick={(e) => {
              e.stopPropagation();
              setApiReports(prev => prev.filter(r => r.id !== row.id));
            }}
          >
            <RiDeleteBinLine />
          </button>
        </div>
      ),
      sortable: false,
      tableColumn: false,
      minWidth: "90px",
      grow: 0,
    }
  ];

  const handleSort = (column, sortOrder) => {
    let field = "";
    if (column.filterName === "reportIdFilter") field = "reportId";
    else if (column.filterName === "reportTitleFilter") field = "reportTitle";
    else if (column.filterName === "scopeFilter") field = "scope";
    else if (column.filterName === "generatedOnFilter") field = "generatedOn";
    else if (column.filterName === "generatedByFilter") field = "assignedDriver";

    setSortColumn(field);
    setSortDirection(sortOrder);
  };

  const handleSearch = (text) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };



  return (
    <div className="fmcr_page">
      <Breadcrumbs values={
        activeView === "tracking" ? ["FMCR", "Fleet Monitoring"] :
          ["FMCR", "FMCR Reports"]
      } />

      <div className="access_view">
        <div className="d-flex align-items-center gap-3">
          <h2>FMCR</h2>

          <div className="access_btn">
            {canViewTracking && (
              <button
                className={activeView === "tracking" ? "active" : ""}
                onClick={() => setActiveView("tracking")}
              >
                Tracking
              </button>
            )}
            {canViewReports && (
              <button
                className={activeView === "reports" ? "active" : ""}
                onClick={() => setActiveView("reports")}
              >
                Reports
              </button>
            )}
          </div>
        </div>
        {activeView === "reports" && canCreateReport && (
          <div className="services_buttons">
            <Button
              submitBtn={() => navigate("/fmcr/generate-new-report")}
              submitText="Generate New Report"
              type="button"
              shouldRow={false}
              isShowCancel={false}
            />
          </div>
        )}
      </div>

      {activeView === "reports" && canViewReports && (
        <div className="fmcr_under_construction">
          <div className="fmcr_under_construction_content">
            <h3>Fleet Monitoring &amp; Control Room (FMCR)</h3>
            <p>
              This module is currently under construction. We are fine-tuning the tracking interfaces and real-time report delivery analytics for a premium experience.
            </p>
            <strong>UNDER CONSTRUCTION</strong>
          </div>
        </div>
      )}

      {activeView === "tracking" && canViewTracking && (
        <FleetMonitoring showHeader={false} />
      )}
    </div>
  );
};

export default FMCR;
